import React from 'react';
import { MenuItem, Stack, TextField, Typography } from '@mui/material';
import CountrySelector from '../../../Components/CountrySelector';
import StateSelector from '../../../Components/StateSelector';
import type { FoundationStudentContact } from '../../../endpoints/foundation';

export type StudentContactValues = FoundationStudentContact;

type Props = {
  value: StudentContactValues;
  onChange: (next: StudentContactValues) => void;
  disabled?: boolean;
  title?: string;
};

const phoneTypes = [
  { value: '', label: '—' },
  { value: 'cell', label: 'Cell' },
  { value: 'home', label: 'Home' },
];

export default function StudentContactFields({
  value,
  onChange,
  disabled = false,
  title = 'Student mailing address & phone',
}: Props) {
  const set = (patch: Partial<StudentContactValues>) =>
    onChange({ ...value, ...patch });

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">{title}</Typography>
      <TextField
        label="Address line 1"
        value={value.student_address_1 || ''}
        onChange={(e) => set({ student_address_1: e.target.value })}
        disabled={disabled}
        fullWidth
      />
      <TextField
        label="Address line 2"
        value={value.student_address_2 || ''}
        onChange={(e) => set({ student_address_2: e.target.value })}
        disabled={disabled}
        fullWidth
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          label="City"
          value={value.student_city || ''}
          onChange={(e) => set({ student_city: e.target.value })}
          disabled={disabled}
          fullWidth
        />
        <TextField
          label="Postal code"
          value={value.student_postal_code || ''}
          onChange={(e) => set({ student_postal_code: e.target.value })}
          disabled={disabled}
          fullWidth
        />
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Stack flex={1} minWidth={0}>
          <CountrySelector
            value={value.student_country_id || ''}
            handleChange={(country: { id?: string } | null) =>
              set({
                student_country_id: country?.id || '',
                student_state_id: '',
              })
            }
            required={false}
            disabled={disabled}
          />
        </Stack>
        <Stack flex={1} minWidth={0}>
          <StateSelector
            value={value.student_state_id || ''}
            handleChange={(state: { id?: string } | null) =>
              set({ student_state_id: state?.id || '' })
            }
            countryId={value.student_country_id || ''}
            required={false}
            disabled={disabled}
          />
        </Stack>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          label="Primary phone"
          value={value.student_primary_phone || ''}
          onChange={(e) => set({ student_primary_phone: e.target.value })}
          disabled={disabled}
          fullWidth
          sx={{ flex: 2 }}
        />
        <TextField
          select
          label="Primary type"
          value={value.student_primary_phone_type || ''}
          onChange={(e) => set({ student_primary_phone_type: e.target.value })}
          disabled={disabled}
          fullWidth
          sx={{ flex: 1 }}
        >
          {phoneTypes.map((t) => (
            <MenuItem key={t.value || 'empty'} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          label="Secondary phone"
          value={value.student_secondary_phone || ''}
          onChange={(e) => set({ student_secondary_phone: e.target.value })}
          disabled={disabled}
          fullWidth
          sx={{ flex: 2 }}
        />
        <TextField
          select
          label="Secondary type"
          value={value.student_secondary_phone_type || ''}
          onChange={(e) =>
            set({ student_secondary_phone_type: e.target.value })
          }
          disabled={disabled}
          fullWidth
          sx={{ flex: 1 }}
        >
          {phoneTypes.map((t) => (
            <MenuItem key={`s-${t.value || 'empty'}`} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    </Stack>
  );
}
